import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";

// Auth Pages
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";

// Layout
import AppLayout from "@/components/layout/AppLayout";

// Dashboard
import DashboardPage from "@/pages/DashboardPage";

// Centre des opérations
import PlanningPage from "@/pages/operations/PlanningPage";
import TimeTrackingPage from "@/pages/operations/TimeTrackingPage";
import FormsPage from "@/pages/operations/FormsPage";
import ChecklistExecutionPage from "@/pages/operations/ChecklistExecutionPage";
import ChecklistReportPage from "@/pages/operations/ChecklistReportPage";
import TasksPage from "@/pages/operations/TasksPage";

// Communication
import MessagesPage from "@/pages/communication/MessagesPage";
import ChatPage from "@/pages/communication/ChatPage";
import NewsPage from "@/pages/communication/NewsPage";
import DirectoryPage from "@/pages/communication/DirectoryPage";
import KnowledgePage from "@/pages/communication/KnowledgePage";
import SupportPage from "@/pages/communication/SupportPage";
import EventsPage from "@/pages/communication/EventsPage";

// Espace RH
import TrainingPage from "@/pages/hr/TrainingPage";
import DocumentsPage from "@/pages/hr/DocumentsPage";
import RecognitionPage from "@/pages/hr/RecognitionPage";
import LeavesPage from "@/pages/hr/LeavesPage";
import OrgChartPage from "@/pages/hr/OrgChartPage";
import EmployeesPage from "@/pages/hr/EmployeesPage";

// Other
import ProfilePage from "@/pages/ProfilePage";
import SettingsPage from "@/pages/SettingsPage";
import NotificationsPage from "@/pages/NotificationsPage";
import UsersPage from "@/pages/UsersPage";
import AdminPage from "@/pages/system/AdminPage";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="spinner" /></div>;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          
          {/* Protected routes */}
          <Route path="/" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            
            {/* Centre des opérations */}
            <Route path="planning" element={<PlanningPage />} />
            <Route path="time-tracking" element={<TimeTrackingPage />} />
            <Route path="forms" element={<FormsPage />} />
            <Route path="checklist/:id" element={<ChecklistExecutionPage />} />
            <Route path="checklist/report/:id" element={<ChecklistReportPage />} />
            <Route path="tasks" element={<TasksPage />} />
            
            {/* Communication */}
            <Route path="messages" element={<MessagesPage />} />
            <Route path="chat" element={<ChatPage />} />
            <Route path="news" element={<NewsPage />} />
            <Route path="directory" element={<DirectoryPage />} />
            <Route path="knowledge" element={<KnowledgePage />} />
            <Route path="support" element={<SupportPage />} />
            <Route path="events" element={<EventsPage />} />
            
            {/* Espace RH */}
            <Route path="training" element={<TrainingPage />} />
            <Route path="documents" element={<DocumentsPage />} />
            <Route path="recognition" element={<RecognitionPage />} />
            <Route path="leaves" element={<LeavesPage />} />
            <Route path="orgchart" element={<OrgChartPage />} />
            <Route path="employees" element={<AdminRoute><EmployeesPage /></AdminRoute>} />
            
            {/* Other */}
            <Route path="profile" element={<ProfilePage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="users" element={<AdminRoute><UsersPage /></AdminRoute>} />
            <Route path="settings" element={<AdminRoute><SettingsPage /></AdminRoute>} />
            <Route path="administration" element={<AdminRoute><AdminPage /></AdminRoute>} />
          </Route>
          
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
